import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts'
import type { HistogramData } from './types'
import { COLORS, FONT_FAMILY } from './theme'

interface DistributionHistogramProps {
  data: HistogramData
  title: string
  xLabel: string
  mode: 'drawdown' | 'profit'
}

function fmtPct(value: number): string {
  return `${(value * 100).toFixed(2)}%`
}

function barColor(mode: DistributionHistogramProps['mode'], value: number): string {
  if (mode === 'drawdown') {
    if (value < -0.005) return COLORS.secondary
    if (value < -0.002) return COLORS.warning
    return COLORS.success
  }
  return value < 0 ? COLORS.secondary : COLORS.success
}

export function DistributionHistogram({ data, title, xLabel, mode }: DistributionHistogramProps) {
  if (!data.bins.length) return null

  const chartData = data.bins.map((bin) => ({
    ...bin,
    midpoint: (bin.binStart + bin.binEnd) / 2,
  }))

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-2">
        <h3 className="font-bold text-[14px]" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
          {title}
        </h3>
        <div className="flex gap-3 text-[10px] mt-1" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
          <span>
            Mediana: <strong style={{ color: COLORS.primary }}>{fmtPct(data.median)}</strong>
          </span>
          <span>
            5th pct: <strong style={{ color: COLORS.secondary }}>{fmtPct(data.p5)}</strong>
          </span>
          <span>
            95th pct: <strong style={{ color: COLORS.success }}>{fmtPct(data.p95)}</strong>
          </span>
        </div>
      </div>
      <div className="flex-1 min-h-0 min-h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
            <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
            <XAxis
              dataKey="midpoint"
              tick={{ fill: COLORS.textSecondary, fontSize: 9, fontFamily: FONT_FAMILY }}
              axisLine={{ stroke: COLORS.border }}
              tickLine={{ stroke: COLORS.border }}
              tickFormatter={fmtPct}
              label={{ value: xLabel, position: 'insideBottom', offset: -5, fill: COLORS.textSecondary, fontSize: 10 }}
            />
            <YAxis
              tick={{ fill: COLORS.textSecondary, fontSize: 9, fontFamily: FONT_FAMILY }}
              axisLine={{ stroke: COLORS.border }}
              tickLine={{ stroke: COLORS.border }}
              label={{ value: 'Frecuencia', angle: -90, position: 'insideLeft', fill: COLORS.textSecondary, fontSize: 10 }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: '0px' }}
              itemStyle={{ fontSize: 11, fontFamily: FONT_FAMILY }}
              formatter={(value, _name, props) => {
                const { binStart, binEnd } = props?.payload as { binStart: number; binEnd: number }
                return [String(value), `${fmtPct(binStart)} - ${fmtPct(binEnd)}`]
              }}
              labelFormatter={() => ''}
            />
            <ReferenceLine x={data.median} stroke={COLORS.primary} strokeWidth={2} />
            <ReferenceLine x={data.p5} stroke={COLORS.secondary} strokeWidth={1.5} strokeDasharray="5 5" />
            <ReferenceLine x={data.p95} stroke={COLORS.success} strokeWidth={1.5} strokeDasharray="5 5" />
            <Bar dataKey="count">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={barColor(mode, entry.midpoint)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
